import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { getAnimationAppearDisappear } from '@examdojo/animation';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { SelectOptionDirective, SelectOptionType, SingleSelectComponent } from '@examdojo/core/select';
import { ToastService } from '@examdojo/core/toast';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { USER_ROLE_ORDER, UserRole } from '@examdojo/models/user';
import { PayoutRateHistoryDialogService, PayoutRateQuery, PayoutRatesService } from '@examdojo/payments';
import { SyllabusPermissionBySyllabusId, UserReviewModeBindingsPermissions } from '@examdojo/review';
import { distinctUntilChanged, EMPTY, filter, finalize, from, map, merge, switchMap, tap } from 'rxjs';
import { PaymentRequestsComponent, PaymentRequestsConfig } from '../../../payment-requests/payment-requests.component';
import { CmsUrlParts } from '../../cms.model';
import { DetailViewWrapperComponent } from '../../detail-view/wrapper/detail-view-wrapper.component';
import { AdminUserQuery } from '../admin-user.query';
import { AdminUserService } from '../admin-user.service';
import { RemoveAccessConfirmationDialogService } from '../remove-access/remove-access-confirmation-dialog.service';
import { ReviewPermissionsListViewComponent } from '../review-permissions-list-view/review-permissions-list-view.component';
import { RoleBadgeComponent } from '../shared/role-badge.component';
import { AdminUserDetailFormService } from './admin-user-detail-form.service';
import { EditPayoutRateDialogComponent } from './edit-payout-rate-dialog.component';

interface RoleOption {
  value: UserRole;
  label: string;
}

@Component({
  selector: 'exam-admin-user-detail-container',
  templateUrl: './admin-user-detail-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [
    DetailViewWrapperComponent,
    DatePipe,
    DecimalPipe,
    ReviewPermissionsListViewComponent,
    IconComponent,
    ButtonComponent,
    MatMenuModule,
    MatTabsModule,
    IconButtonComponent,
    SingleSelectComponent,
    SelectOptionDirective,
    RoleBadgeComponent,
    ReactiveFormsModule,
    PaymentRequestsComponent,
  ],
  animations: [getAnimationAppearDisappear()],
  providers: [AdminUserDetailFormService, RemoveAccessConfirmationDialogService],
})
export class AdminUserDetailContainerComponent {
  constructor(
    private readonly adminUserQuery: AdminUserQuery,
    private readonly adminUserService: AdminUserService,
    private readonly adminUserDetailFormService: AdminUserDetailFormService,
    private readonly payoutRatesService: PayoutRatesService,
    private readonly payoutRateHistoryDialogService: PayoutRateHistoryDialogService,
    private readonly payoutRateQuery: PayoutRateQuery,
    private readonly destroyRef: DestroyRef,
    private readonly toastService: ToastService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly removeAccessConfirmationDialogService: RemoveAccessConfirmationDialogService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
  ) {
    merge(this.updateRoleOnChange()).pipe(takeUntilDestroyed()).subscribe();
  }

  @ViewChild(ReviewPermissionsListViewComponent, { static: false })
  reviewPermissionsComponent!: ReviewPermissionsListViewComponent;

  readonly CmsUrlParts = CmsUrlParts;

  readonly form = this.adminUserDetailFormService.form;
  readonly reviewPermissionsForm = this.adminUserDetailFormService.reviewPermissionsForm;
  readonly roleControl = this.adminUserDetailFormService.roleControl;
  readonly searchCtrl = new FormControl<string>('', { nonNullable: true });

  readonly roleOptions = USER_ROLE_ORDER.filter((role) => role === 'ADMIN' || role === 'CONTRIBUTOR').map((role) => ({
    value: role,
    label: role,
  }));

  // Type variable for template type safety
  readonly selectOptionType!: SelectOptionType<RoleOption>;

  readonly isSaving = signal<boolean>(false);

  readonly state = connectState({
    adminUser: this.adminUserQuery.active$,
    isLoading: this.adminUserQuery.selectLoading(),
    initialized: this.adminUserQuery.selectActive().pipe(map((adminUser) => isNotNullish(adminUser))),
    payoutRate: this.adminUserQuery.active$.pipe(map((adminUser) => adminUser?.payoutRate)),
    isUserAdmin: this.adminUserQuery.isUserAdmin$,
    isCurrentUserSelected: this.adminUserQuery.isCurrentUserSelected$,
  });

  readonly paymentRequestsConfig = computed((): PaymentRequestsConfig => {
    const userId = this.state.adminUser?.id || ''; // Safe because component only renders when ID exists
    return {
      userId,
      showHeader: false,
      showStatusFilter: true,
      defaultStatusFilter: 'ALL',
      headerTitle: 'Payment History',
    };
  });

  save() {
    this.isSaving.set(true);

    this.adminUserDetailFormService
      .updateUserReviewPermissions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((selectedReviewPermissions: SyllabusPermissionBySyllabusId[]) => {
          this.reviewPermissionsForm.markAsPristine();

          const userReviewModeBindingsPermissions: UserReviewModeBindingsPermissions[] = selectedReviewPermissions.map(
            (permission) => ({
              syllabus_id: permission.syllabusId,
              ...permission.permissions,
            }),
          );

          this.adminUserService.updateStoreUserReviewPermissions(userReviewModeBindingsPermissions);
          this.toastService.success('Review permissions updated');
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY, { showErrorDetailsInToast: true }),
        finalize(() => this.isSaving.set(false)),
      )
      .subscribe();
  }

  removeAccess() {
    return from(this.removeAccessConfirmationDialogService.openDialog())
      .pipe(
        filter(Boolean),
        tap(() => this.router.navigate(['/', CmsUrlParts.Admin])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  openEditRateDialog() {
    const adminUser = this.state.adminUser;
    if (!adminUser || !adminUser.id) {
      return;
    }

    // Get the latest valid_from date from user's rates (rates are sorted by valid_from desc)
    const userRates = this.payoutRateQuery.getUserRates(adminUser.id);
    const latestValidFrom = userRates.length > 0 && userRates[0].valid_from ? userRates[0].valid_from : null;

    const dialogRef = this.dialog.open(EditPayoutRateDialogComponent, {
      width: '460px',
      data: {
        userName: adminUser.first_name ? `${adminUser.first_name} ${adminUser.last_name}` : 'this user',
        currentRate: adminUser.payoutRate,
        latestValidFrom,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        switchMap((result) => {
          assertNonNullable(adminUser.id, 'adminUser.id');
          return this.payoutRatesService.updateUserRate(adminUser.id, result.rate, result.validFrom).pipe(
            tap(() => {
              this.toastService.success('Payout rate updated successfully');
            }),
            this.errorHandlerService.catchHttpErrors(() => EMPTY),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  viewRateHistory() {
    const adminUser = this.state.adminUser;
    if (adminUser?.id) {
      this.payoutRateHistoryDialogService.openDialog({
        userId: adminUser.id,
        userName: adminUser.first_name ? `${adminUser.first_name} ${adminUser.last_name}` : undefined,
      });
    }
  }

  private updateRoleOnChange() {
    return this.roleControl.valueChanges.pipe(
      distinctUntilChanged(),
      switchMap((role) => {
        const activeUser = this.adminUserQuery.getActive();
        assertNonNullable(activeUser?.id, 'activeUser.id');

        return this.adminUserService.updateUserRole(activeUser.id, role).pipe(
          tap(() => {
            this.toastService.success('User role updated');
          }),
        );
      }),
    );
  }
}
